import { Association, AssociationKeys } from '../utils/association.model';
import { InviteeAgeGroup } from './registrants.model';
import { FlightInformationModel } from './flight-information.model';
import { SelectedExcursions } from './excursion.model';
import { LookupKeys } from '../crm/lookup.model';
import { TShirtSizesKeys } from '../utils/t-shirt-sizes.model';
import { DietaryConsiderationKeys } from '../utils/dietary-consideration.model';

export enum GuestKeys {
  conferenceDbId = 'conferenceDbId',
  registrantDbId = 'registrantDbId',
  associationDbId = 'associationDbId',
  complimentaryConfiguration = 'complimentaryConfiguration',
  approved = 'approved',
  approvedBy = 'approved_by',
  approvedDate = 'approved_date',
  revokedBy = 'revoked_by',
  revokedDate = 'revoked_date',
  childOrAdult = 'child_or_adult',
  flightInformation = 'flightInformation',
  selectedExcursions = 'selectedExcursions',
  badgeName = 'badgeName',
  guestStatus = 'guestStatus',
  guestEvents = 'guestEvents',
  chairmanExtSelectedExcursions = 'chairmanExtSelectedExcursions',
}

export enum GuestEvents {
  main = 'main',
  chairmanExtension = 'chairmanExtension',
}

export type GuestComplimentaryForConfigurationType = {
  [key in keyof typeof GuestEvents]?: boolean;
};

export class GuestComplimentaryForConfiguration implements GuestComplimentaryForConfigurationType {}

export const GuestEventsMap = new Map([
  [GuestEvents.main, 'Main Event'],
  [GuestEvents.chairmanExtension, 'Chairman Extension'],
]);

export const GUEST_EVENTS_LOOKUP = [GuestEvents.main, GuestEvents.chairmanExtension].map(status => {
  return { [LookupKeys.value]: status, [LookupKeys.description]: GuestEventsMap.get(status) };
});

export enum GuestStatus {
  default = 'default',
  deleted = 'deleted',
  canceled = 'canceled',
}

export const GuestStatusMap = new Map([
  [GuestStatus.default, 'Invited'],
  [GuestStatus.deleted, 'Deleted'],
  [GuestStatus.canceled, 'Canceled'],
]);

export const GUEST_STATUS_LOOKUP = [GuestStatus.default, GuestStatus.canceled].map(status => {
  return { [LookupKeys.value]: status, [LookupKeys.description]: GuestStatusMap.get(status) };
});

export const ConferenceRegistrationGuestAssociationKeysToPick = [
  AssociationKeys.prefix,
  AssociationKeys.firstName,
  AssociationKeys.lastName,
  AssociationKeys.suffix,
  AssociationKeys.gender,
  AssociationKeys.dob,
  AssociationKeys.associationType,
  AssociationKeys.tShirtSizes,
  AssociationKeys.dietaryConsideration,
  AssociationKeys.flightPreference,
] as const;

export const GUEST_TYPES_LOOKUP = [
  { [LookupKeys.value]: true, [LookupKeys.description]: 'Complimentary' },
  { [LookupKeys.value]: false, [LookupKeys.description]: 'Additional' },
];

export class GuestData extends Association {
  [GuestKeys.conferenceDbId]?: string = '';
  [GuestKeys.registrantDbId]?: string = '';
  [GuestKeys.associationDbId]?: string = null;
  [GuestKeys.complimentaryConfiguration]?: GuestComplimentaryForConfiguration = {
    [GuestEvents.main]: false,
    [GuestEvents.chairmanExtension]: false,
  };
  [GuestKeys.approved]?: boolean = false;
  [GuestKeys.approvedBy]?: string = '';
  [GuestKeys.approvedDate]?: Date = null;
  [GuestKeys.revokedBy]?: string = '';
  [GuestKeys.revokedDate]?: Date;
  [GuestKeys.childOrAdult]?: InviteeAgeGroup = null;
  [GuestKeys.badgeName]?: string;
  [GuestKeys.guestStatus]?: GuestStatus = GuestStatus.default;
  [GuestKeys.guestEvents]?: GuestEvents[] = [GuestEvents.main];

  // Flight configuration
  [GuestKeys.flightInformation]?: FlightInformationModel;
  [GuestKeys.selectedExcursions]?: SelectedExcursions;
  [GuestKeys.chairmanExtSelectedExcursions]?: SelectedExcursions;
}

export const GUEST_TITLE_LIST = {
  // Event Information
  [GuestKeys.approvedDate]: 'Approved Date',
  [GuestKeys.approvedBy]: 'Approved By',

  // Registrant Information
  [AssociationKeys.firstName]: 'First Name',
  [AssociationKeys.lastName]: 'Last Name',
  [AssociationKeys.prefix]: 'Prefix',
  [AssociationKeys.suffix]: 'Suffix',
  [AssociationKeys.associationType]: 'Relationship',
  [AssociationKeys.gender]: 'Gender',
  [AssociationKeys.dob]: 'Date of Birth',

  // Additional Information
  [TShirtSizesKeys.unisexTShirtSize]: 'Unisex T-Shirt Size',
  [GuestKeys.childOrAdult]: 'Child or Adult',
  // Dietary Information
  [DietaryConsiderationKeys.hasDietaryOrPersonalConsideration]: 'Dietary or Personal Considerations?',
  [DietaryConsiderationKeys.dietaryConsiderationType]: 'Dietary Consideration Type',
  [DietaryConsiderationKeys.dietaryConsideration]: 'Dietary Consideration',
};
